import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useCallback } from 'react';

import { useResolveCallout } from '@/hooks/useResolveCallout';
import { userIdAtom } from '@/services/globalState';
import {
  getUserCalloutsQueryOptions,
  selectCallout,
  shouldSuppressCallout,
  type CalloutKey,
} from '@/services/userCallouts';

type UseCalloutBannerParams = {
  /** The callout this banner is gated on. */
  key: CalloutKey;
  /**
   * Caller-side eligibility (feature flag, role, inquiry state). The banner
   * never shows when false, regardless of callout state.
   */
  enabled: boolean;
};

/**
 * Server-backed "show once, then never again" banner gate — the user-callouts
 * counterpart to the device-local `useInboxFeatureBanner`. Returns the same
 * `{ isVisible, dismiss }` shape so a banner can swap between them.
 *
 * Shows when the caller is eligible and the user has not resolved the callout;
 * `dismiss` records a `resolve`, so the banner stays gone across the user's
 * devices instead of resetting per device/browser. This fits one-time intros and
 * announcements; for a nudge that de-escalates (modal → banner → silenced),
 * derive the stage from the callout directly instead. When several composer
 * callouts can be eligible at once, use `useComposerCalloutBanner` instead so
 * only one shows.
 *
 * Fail-closed via `shouldSuppressCallout`: while the callouts query is loading or
 * errored the banner stays hidden, so an outage never re-surfaces a banner the
 * user already dismissed. The dismissal optimistically resolves the cached row so
 * the banner can't flash back before the refetch lands, and rolls back on error.
 */
export function useCalloutBanner({ key, enabled }: UseCalloutBannerParams) {
  const userId = useAtomValue(userIdAtom);

  const { data: callouts, isSuccess: calloutsLoaded } = useQuery(
    getUserCalloutsQueryOptions(userId)
  );

  const isVisible = enabled && !shouldSuppressCallout(calloutsLoaded, selectCallout(callouts, key));

  const resolve = useResolveCallout();

  const dismiss = useCallback(() => {
    resolve(key);
  }, [resolve, key]);

  return { isVisible, dismiss };
}
